import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Button,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import AddWorkoutDetails from "../AddWorkoutDetails";
import Loading from "../Layout/Loading";
import {
  exerciseMatchesDraft,
  exerciseNameSetFromWorkouts,
} from "../../utils/exerciseNameMatch";
import {
  clientProgramStatusFromWorkouts,
  programUrgencyColor,
  programUrgencyLabel,
} from "../../utils/clientProgramStatus";
import { outlineToWorkouts } from "../../utils/outlineToWorkout";
import ProgramOutlineEditor, { newLine } from "./ProgramOutlineEditor";

function workoutsInWeek(workouts, weekContainingMoment) {
  const ws = weekContainingMoment.clone().startOf("week");
  const we = weekContainingMoment.clone().endOf("week");
  return workouts.filter((w) => {
    const d = moment.unix(w.date);
    return d.isBetween(ws, we, "day", "[]");
  });
}

function resolveCircuitRounds(exercise) {
  if (Array.isArray(exercise.circuit) && exercise.circuit.length > 0) {
    return exercise.circuit.length;
  }
  const r = exercise.rounds;
  if (typeof r === "number" && r > 0) return r;
  if (r !== undefined && r !== null && r !== "") {
    const n = parseInt(String(r), 10);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  return 1;
}

/** Names for one round template (first round only — same pattern repeats). */
function circuitTemplateNames(exercise) {
  if (Array.isArray(exercise.circuit) && exercise.circuit.length > 0) {
    const round0 = exercise.circuit[0];
    if (Array.isArray(round0)) {
      const names = round0
        .map((ex) => (ex?.name ? String(ex.name).trim() : ""))
        .filter(Boolean);
      if (names.length > 0) return names;
    }
  }
  if (Array.isArray(exercise.subExercises) && exercise.subExercises.length > 0) {
    return exercise.subExercises
      .map((sub) => (sub?.name ? String(sub.name).trim() : ""))
      .filter(Boolean);
  }
  return [];
}

function isCircuitExercise(exercise) {
  if (!exercise) return false;
  const t = String(exercise.exerciseType || exercise.type || "").toLowerCase();
  if (t === "single") return false;
  if (t === "circuit") return true;
  if (Array.isArray(exercise.circuit) && exercise.circuit.length > 0) {
    const r0 = exercise.circuit[0];
    return Array.isArray(r0) && r0.length > 0;
  }
  if (Array.isArray(exercise.subExercises) && exercise.subExercises.length > 0) {
    return true;
  }
  return false;
}

/**
 * One summary line per exercise block: single = name; circuit = one line with rounds + names.
 */
function formatExerciseSummaryLines(exercise) {
  if (!exercise) return [];

  if (isCircuitExercise(exercise)) {
    const rounds = resolveCircuitRounds(exercise);
    const roundLabel = rounds === 1 ? "1 round" : `${rounds} rounds`;
    const names = circuitTemplateNames(exercise);
    if (names.length > 0) {
      return [`Circuit (${roundLabel}): ${names.join(", ")}`];
    }
    const parent = exercise.name && String(exercise.name).trim();
    if (parent) {
      return [`Circuit (${roundLabel}): ${parent}`];
    }
    return [`Circuit (${roundLabel})`];
  }

  const name = exercise.name && String(exercise.name).trim();
  if (name) return [name];
  return ["(Untitled exercise)"];
}

const ProgramCreator = () => {
  const { getClients, getWorkoutsForUser, addWorkoutForUser } = useApi();
  const { id: coachId, name: coachName } = useUserInfo();
  const [searchParams, setSearchParams] = useSearchParams();

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientId, setClientId] = useState("");
  const [clientProgramStatuses, setClientProgramStatuses] = useState({});
  const [loadingProgramStatuses, setLoadingProgramStatuses] = useState(false);
  const [weekAnchor, setWeekAnchor] = useState(() => moment().startOf("day"));
  const [workouts, setWorkouts] = useState([]);
  const [workoutsLoading, setWorkoutsLoading] = useState(false);
  const [draftExerciseNames, setDraftExerciseNames] = useState(() => new Set());

  const weekStartMs = weekAnchor.clone().startOf("week").valueOf();

  const handleDraftExerciseNamesChange = useCallback((set) => {
    setDraftExerciseNames(set instanceof Set ? set : new Set());
  }, []);

  const fetchClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const c = await getClients();
      const active = (c || []).filter((client) => !client.deleted);
      setClients(active);
    } catch (e) {
      console.error(e);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, [getClients]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const coachClientsOnly = useMemo(() => {
    if (!coachId || !coachName) return clients;
    return clients.filter((c) => c.id !== coachId);
  }, [clients, coachId, coachName]);

  useEffect(() => {
    const cid = searchParams.get("clientId");
    if (cid && coachClientsOnly.some((c) => c.id === cid)) {
      setClientId(cid);
    } else {
      setClientId("");
    }
  }, [searchParams, coachClientsOnly]);

  const fetchClientProgramStatuses = useCallback(async () => {
    if (coachClientsOnly.length === 0) {
      setClientProgramStatuses({});
      return;
    }
    setLoadingProgramStatuses(true);
    const from = moment().subtract(12, "weeks").startOf("week").unix();
    const to = moment().add(6, "months").endOf("week").unix();
    try {
      const results = await Promise.all(
        coachClientsOnly.map(async (client) => {
          try {
            const data = await getWorkoutsForUser(client.id, from, to);
            const status = clientProgramStatusFromWorkouts(
              Array.isArray(data) ? data : []
            );
            return [client.id, status];
          } catch (e) {
            console.error(e);
            return [client.id, null];
          }
        })
      );
      setClientProgramStatuses(Object.fromEntries(results));
    } finally {
      setLoadingProgramStatuses(false);
    }
  }, [coachClientsOnly, getWorkoutsForUser]);

  useEffect(() => {
    fetchClientProgramStatuses();
  }, [fetchClientProgramStatuses]);

  const clientsByProgramUrgency = useMemo(() => {
    return [...coachClientsOnly].sort((a, b) => {
      const statusA = clientProgramStatuses[a.id];
      const statusB = clientProgramStatuses[b.id];
      const weeksA = statusA?.weeksRemaining;
      const weeksB = statusB?.weeksRemaining;
      if (weeksA == null && weeksB == null) return a.name.localeCompare(b.name);
      if (weeksA == null) return 1;
      if (weeksB == null) return -1;
      if (weeksA !== weeksB) return weeksA - weeksB;
      return a.name.localeCompare(b.name);
    });
  }, [coachClientsOnly, clientProgramStatuses]);

  const selectClient = useCallback(
    (id) => {
      setSearchParams(id ? { clientId: id } : {}, { replace: false });
      if (!id) fetchClientProgramStatuses();
    },
    [setSearchParams, fetchClientProgramStatuses]
  );

  const showClientPicker = !clientId;

  const prevOutlineClientRef = useRef(undefined);
  useEffect(() => {
    if (
      prevOutlineClientRef.current !== undefined &&
      prevOutlineClientRef.current !== clientId
    ) {
      setOutlineLines([newLine()]);
      setAppliedOutlineWorkouts(null);
    }
    prevOutlineClientRef.current = clientId;
  }, [clientId]);

  const prevClientIdRef = useRef(undefined);
  useEffect(() => {
    const prev = prevClientIdRef.current;
    if (prev && clientId && prev !== clientId) {
      setDraftExerciseNames(new Set());
    }
    prevClientIdRef.current = clientId;
  }, [clientId]);

  const fetchWeekWorkouts = useCallback(async () => {
    if (!clientId) {
      setWorkouts([]);
      return;
    }
    setWorkoutsLoading(true);
    try {
      const from = moment(weekStartMs)
        .subtract(1, "week")
        .startOf("week")
        .unix();
      const to = moment(weekStartMs).endOf("week").unix();
      const data = await getWorkoutsForUser(clientId, from, to);
      setWorkouts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setWorkouts([]);
    } finally {
      setWorkoutsLoading(false);
    }
  }, [clientId, weekStartMs, getWorkoutsForUser]);

  useEffect(() => {
    fetchWeekWorkouts();
  }, [fetchWeekWorkouts]);

  const selectedWeekWorkouts = useMemo(
    () => workoutsInWeek(workouts, moment(weekStartMs)),
    [workouts, weekStartMs]
  );

  const previousWeekWorkouts = useMemo(
    () => workoutsInWeek(workouts, moment(weekStartMs).subtract(1, "week")),
    [workouts, weekStartMs]
  );

  const previousWeekExerciseNameSet = useMemo(
    () => exerciseNameSetFromWorkouts(previousWeekWorkouts),
    [previousWeekWorkouts]
  );

  const weekLabel = useMemo(() => {
    const start = moment(weekStartMs).startOf("week");
    const end = moment(weekStartMs).endOf("week");
    return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
  }, [weekStartMs]);

  const daysSummary = useMemo(() => {
    const start = moment(weekStartMs).startOf("week");
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = start.clone().add(i, "day");
      const dayWorkouts = selectedWeekWorkouts.filter((w) =>
        moment.unix(w.date).isSame(day, "day")
      );
      days.push({ day, workouts: dayWorkouts });
    }
    return days;
  }, [weekStartMs, selectedWeekWorkouts]);

  const daysSummaryWithWorkouts = useMemo(
    () => daysSummary.filter(({ workouts }) => workouts.length > 0),
    [daysSummary]
  );

  const [saveNonce, setSaveNonce] = useState(0);
  const [outlineLines, setOutlineLines] = useState(() => [newLine()]);
  const [outlineApplyNonce, setOutlineApplyNonce] = useState(0);
  const [appliedOutlineWorkouts, setAppliedOutlineWorkouts] = useState(null);

  const buildDraftWorkout = useCallback(
    (outlineWorkout, formIndex) => {
      const base = {
        name: "",
        description: "",
        exercises: [],
        date: moment(weekStartMs).add(7 + formIndex, "days"),
      };
      if (!outlineWorkout) return base;
      return {
        ...base,
        name: outlineWorkout.name || base.name,
        description: outlineWorkout.description || "",
        exercises: outlineWorkout.exercises || [],
        date: base.date,
      };
    },
    [weekStartMs]
  );

  const workoutFormsToRender = useMemo(() => {
    if (appliedOutlineWorkouts !== null) {
      return appliedOutlineWorkouts.map((w, i) => ({
        key: `outline-${i}`,
        outline: w,
        index: i,
      }));
    }
    return [{ key: "default", outline: null, index: 0 }];
  }, [appliedOutlineWorkouts]);

  const handleApplyOutline = () => {
    setAppliedOutlineWorkouts(outlineToWorkouts(outlineLines));
    setOutlineApplyNonce((n) => n + 1);
  };

  const handleSaveWorkout = async (workoutData, formIndex) => {
    await addWorkoutForUser(clientId, workoutData);
    await fetchWeekWorkouts();
    setSaveNonce((n) => n + 1);
    setAppliedOutlineWorkouts((prev) => {
      if (!prev) return null;
      const next = prev.filter((_, i) => i !== formIndex);
      return next.length === 0 ? null : next;
    });
  };

  if (loadingClients) {
    return (
      <Box sx={{ mt: 10, display: "flex", justifyContent: "center" }}>
        <Loading />
      </Box>
    );
  }

  const noClients = coachClientsOnly.length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "calc(100vh - 72px)",
        mt: 1,
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="h5" component="h1" sx={{ mb: 0.5 }}>
          Program creator
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick a week to see scheduled workouts, then draft in the outline or
          fill the workout form. The exercise list comes from the Functional
          Fitness database (bundled JSON; run{" "}
          <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
            npm run build:exercise-db
          </Typography>{" "}
          to refresh from the public Google Sheet).
        </Typography>

        {noClients ? (
          <Paper sx={{ p: 3 }}>
            <Typography>
              Add a client from <strong>Clients</strong> to use the program
              creator.
            </Typography>
          </Paper>
        ) : showClientPicker ? (
          <Paper sx={{ p: 2, maxWidth: 560 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
              Clients by program end date
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sorted by who needs a new program soonest. Click a client to open
              the program creator.
            </Typography>
            {loadingProgramStatuses ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <List disablePadding dense>
                {clientsByProgramUrgency.map((client) => {
                  const status = clientProgramStatuses[client.id];
                  const weeksRemaining = status?.weeksRemaining;
                  const nameColor = programUrgencyColor(weeksRemaining);
                  const secondary = status
                    ? `Week ${status.currentWeek} of ${status.programLength} · ${programUrgencyLabel(weeksRemaining)}`
                    : programUrgencyLabel(null);
                  return (
                    <ListItemButton
                      key={client.id}
                      onClick={() => selectClient(client.id)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        border: 1,
                        borderColor: "divider",
                      }}
                    >
                      <ListItemText
                        primary={client.name}
                        secondary={secondary}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: nameColor,
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Paper>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <IconButton
                size="small"
                onClick={() => selectClient("")}
                aria-label="Back to client list"
              >
                <ArrowBackIcon />
              </IconButton>
              <FormControl fullWidth size="small" sx={{ maxWidth: 420 }}>
                <InputLabel id="program-creator-client-label">Client</InputLabel>
                <Select
                  labelId="program-creator-client-label"
                  label="Client"
                  value={clientId}
                  onChange={(e) => selectClient(e.target.value)}
                >
                  {coachClientsOnly.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name} ({c.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        )}
      </Box>

      {!noClients && clientId && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: "stretch",
            flex: 1,
            minHeight: 0,
            gap: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: { xs: "100%", lg: "38%" },
              flexShrink: 0,
              borderRight: { lg: 1 },
              borderColor: "divider",
              borderRadius: 0,
              p: 2,
              overflow: "auto",
              maxHeight: { xs: "none", lg: "calc(100vh - 200px)" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                mb: 2,
              }}
            >
              <IconButton
                size="small"
                onClick={() =>
                  setWeekAnchor((w) => w.clone().subtract(1, "week"))
                }
                aria-label="Previous week"
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography
                variant="subtitle2"
                sx={{ px: 1, minWidth: 0, flex: 1, textAlign: "center" }}
                fontWeight={600}
              >
                {weekLabel}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setWeekAnchor((w) => w.clone().add(1, "week"))
                }
                aria-label="Next week"
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {workoutsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : daysSummaryWithWorkouts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No workouts this week.
              </Typography>
            ) : (
              <>
                {daysSummaryWithWorkouts.map(({ day, workouts: dayWorkouts }) => (
                  <Box
                    key={day.format("YYYY-MM-DD")}
                    sx={{
                      mb: 1.5,
                      pb: 1.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {day.format("dddd, MMM D")}
                    </Typography>
                    {dayWorkouts.map((w) => {
                      const exercises = w.exercises || [];
                      return (
                        <Box key={w.id} sx={{ mt: 0.5 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {w.name || "Untitled workout"}
                          </Typography>
                          {exercises.length === 0 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: "0.8rem" }}
                            >
                              No exercises
                            </Typography>
                          ) : (
                            <Box
                              component="ul"
                              sx={{
                                m: 0,
                                mt: 0.25,
                                pl: 2,
                                "& li": { py: 0.1 },
                              }}
                            >
                              {exercises.map((ex, exIdx) => {
                                const lines = formatExerciseSummaryLines(ex);
                                const lineHighlight = exerciseMatchesDraft(
                                  ex,
                                  draftExerciseNames
                                );
                                return lines.map((line, lineIdx) => (
                                  <li key={`${w.id}-ex-${exIdx}-${lineIdx}`}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      component="span"
                                      sx={{
                                        fontSize: "0.8rem",
                                        lineHeight: 1.45,
                                        ...(lineHighlight && {
                                          px: 0.5,
                                          py: 0.25,
                                          borderRadius: 0.5,
                                          display: "inline-block",
                                          bgcolor: (theme) =>
                                            theme.palette.mode === "dark"
                                              ? "rgba(255, 167, 38, 0.22)"
                                              : "rgba(237, 108, 2, 0.18)",
                                        }),
                                      }}
                                    >
                                      {line}
                                    </Typography>
                                  </li>
                                ));
                              })}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </>
            )}
          </Paper>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: { xs: "70vh", lg: "calc(100vh - 200px)" },
              overflow: "auto",
              borderTop: { xs: 1, lg: 0 },
              borderColor: "divider",
            }}
          >
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }} fontWeight={600}>
                Program outline
              </Typography>
              <ProgramOutlineEditor
                lines={outlineLines}
                onChange={setOutlineLines}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1.5 }}
                onClick={handleApplyOutline}
              >
                Apply outline to workout form
                {appliedOutlineWorkouts && appliedOutlineWorkouts.length > 1
                  ? "s"
                  : ""}
              </Button>
              {appliedOutlineWorkouts && appliedOutlineWorkouts.length > 1 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  {appliedOutlineWorkouts.length} workouts from outline — save
                  each form below.
                </Typography>
              )}
            </Box>
            {workoutFormsToRender.map(({ key, outline, index }) => (
              <Box
                key={`${clientId}-${key}-${saveNonce}-${outlineApplyNonce}`}
                sx={{
                  borderTop: index > 0 ? 1 : 0,
                  borderColor: "divider",
                  pt: index > 0 ? 2 : 0,
                  mt: index > 0 ? 2 : 0,
                }}
              >
                {appliedOutlineWorkouts && appliedOutlineWorkouts.length > 1 && (
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ px: 2, mb: 1 }}
                  >
                    {outline?.name?.trim() || `Workout ${index + 1}`}
                  </Typography>
                )}
                <AddWorkoutDetails
                  key={`${clientId}-${saveNonce}-${outlineApplyNonce}-${index}-${buildDraftWorkout(outline, index).date.valueOf()}`}
                  open
                  handleClose={() => {}}
                  userId={clientId}
                  workout={buildDraftWorkout(outline, index)}
                  onSave={(data) => handleSaveWorkout(data, index)}
                  onWorkoutCopied={fetchWeekWorkouts}
                  onWorkoutDeleted={fetchWeekWorkouts}
                  onWorkoutUpdated={fetchWeekWorkouts}
                  inline
                  submitButtonId={
                    appliedOutlineWorkouts && appliedOutlineWorkouts.length > 1
                      ? `program-creator-save-workout-${index}`
                      : "program-creator-save-workout"
                  }
                  previousWeekExerciseNameSet={previousWeekExerciseNameSet}
                  onDraftExerciseNamesChange={handleDraftExerciseNamesChange}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProgramCreator;
